/* eslint-disable max-lines */
import React, {
    ReactElement,
    memo,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useEffect,
    RefObject,
    useState,
} from "react";

// components
import {
    NewLocationWidget,
    AddressSelectorWidget,
    UnServiceableCartFooterWidget,
    LoginWidget,
    PaymentWidget,
    AddSelectAddressWidget,
    PremiseSelectOrderType,
    EnterPnr,
    CartSavingsFooterStrip,
} from "@swiggy-private/cart-ui";
import { TurboPaymentCartWrapper } from "@cart-client/components/turbo-payment-cart-wrapper";
import { ErrorWidget } from "@swiggy-private/core-ui";

// helpers
import { NavigationScreenLinkTypes, NavigationTypes } from "@swiggy-private/native-bridge";
import { getTurboPaymentVariantForCart, isTurboPayDiscountValueEnabled } from "@cart-client/helpers/TurboPayment";
import { isEmpty } from "@swiggy-private/js-utils";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";
import { getStaticMwebCartPath } from "@cart-client/helpers/Paths";
import { setCartFromLoginFlowFlag } from "@cart-client/helpers/Login";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// hooks
import { useFetchAddressOnClick } from "@cart-client/hooks/useFetchAddressOnClick";
import { useGiftingCartFooterActions } from "@cart-client/hooks/useGiftingCartFooterActions";
import { usePnrData } from "@cart-client/hooks/usePnrData";
import { useIrctcAnalytics } from "@cart-client/hooks/useIrctcAnalytics";
import { useTurboFooterReady } from "@cart-client/hooks/useTurboFooterReady";

import {
    captureError,
    isiOSPlatform,
    Portal,
    resetElementHeightInDom,
    setElementHeightInDom,
    STYLE_SHEET_VAR,
    format,
} from "@swiggy-private/core-ui";
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { syncClientHeaders } from "@cart-client/helpers/Header";
import { getOnPremiseFulfilmentData, isOnPremiseFulfilmentCart } from "@cart-client/helpers/OnPremiseFulfilment";
import { getAvailableCartItems } from "@cart-client/helpers/Cart";
import { getMROTotalItemsCount } from "@cart-client/helpers/MROFoodCart";
import { isQuickCheckoutFlow } from "@cart-client/helpers/QuickCheckoutCart";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";
import { buildIrctcStationChangeUrl } from "@cart-client/helpers/Irctc";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { IRCTC_STATION_CHANGE_DEEPLINK } from "@cart-client/constants/Irctc";
import {
    getAddressConfugration,
    goToSimilarRestuarantAnalyticsContext,
    goToSimilarRestuarantHomeAnalyticsContext,
} from "@cart-client/helpers/CartFooter";
import { CartOverlaysContext, goToGiftingHome, isJioHotstarFlow } from "@swiggy-private/checkout-core";

// interfaces
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import {
    CART_ERROR_CTA,
    ReviewedCartApiResponse,
    CartItem,
    IDeliverySlots,
    ItemLimitExceededItem,
    CroutonPriority,
    CroutonIds,
    ICartSavingsFooterDetails,
} from "@swiggy-private/cart-types";

// context
import { CookingInstructionsFocusContext } from "@cart-client/contexts/CookingInstructionsFocusContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
import { CartContext } from "@cart-client/contexts/CartContext";

import {
    GlobalHeaderState,
    SwgyHeaderKeysList,
    AnalyticsContext,
    getBridgeSdk,
    getUserInfo,
    UserContext,
    recordNREvent,
    NEWRELIC_TABLES,
    manageSwipe,
    isPopFlow,
    isIxigoFlow,
    closeIxigoWebview,
} from "@swiggy-private/checkout-core";
import {
    clickLogin,
    clickClearCart,
    clickRemoveAllOOSItems,
    clickViewSimilarRestaurantCTA,
    impressionViewSimilarRestaurantCTA,
    impressionViewSimilarRestaurantCTAMRO,
    onImpressionProceedToPay,
    clickSelectDeliverySlot,
    clickDropptDeliverySlot,
    impressionProceedToPay,
    impressionSelectDeliverySlot,
    impressionDelierySlots,
    clickAddMoreItemsCTA,
    onImpressionOcassionsProceedToPay,
    onImpressionTurboStrip,
    onClickTurboStrip,
    onImpressionTurboTooltip,
    onClickTurboTooltipClose,
    impressionRemoveAllOOSItems,
    impressionRemoveAllOOSItemsMRO,
} from "@cart-client/analytics/events";
import { CLICK_ACTIONS, IMPRESSION_ACTIONS } from "@cart-client/analytics/constants";
import { GOT_IT } from "@cart-client/constants/index";
import { CtaAction } from "@cart-client/interfaces/Cart";
import useFetchAddressList from "@cart-client/hooks/useFetchAddressList";
import { IIrctcPayload } from "@swiggy-private/cart-types";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { useCartAddressNudgeAnalytics } from "@cart-client/hooks/useCartAddressNudgeAnalytics";
import { CartFooterPaymentWidgets, TurboPaymentWidgetShimmerV2 } from "@swiggy-private/payment-client";

//styles
import { FooterContainer } from "./CartFooter.styled";
import styles from "./CartFooter.scss";
import { PnrDataContext } from "@cart-client/contexts/PnrDataContext";

interface CartFooterProps {
    className?: string;
    cartTotal: number;
    tipAmount?: number;
    addressId: ReviewedCartApiResponse["address_id"];
    addresses: ReviewedCartApiResponse["addresses"];
    crouton: ReviewedCartApiResponse["crouton"];
    canShowCrouton?: boolean;
    statusCode: CART_STATUS_CODES;
    statusMessage?: string;
    ctaText?: string;
    ctaAction?: CtaAction;
    titleMessage?: string;
    ctaIcon?: string;
    unServiceableItemsCount?: number;
    onMakePayment: (selectedSlot?: string) => void;
    canPlaceOrderFromCart: boolean;
    viewDetailedBill: VoidFunction;
    cartItems: CartItem[];
    makePaymentButtonRef?: RefObject<HTMLDivElement>;
    qFoodSLA?: string;
    deliverySlots?: IDeliverySlots;
    restaurantId?: string | number;
    cartId?: number;
    isTurboPayTooltipHidden: boolean;
    setCroutonVisibility: (croutonVisibility: boolean) => void;
    isTurboPaymentEnabled: boolean;
    setIsTurboPayWidgetHidden: (hideTurboPayWidget: boolean) => void;
    updateCartInProgress: boolean;
    backgroundRefreshInProgress?: boolean;
    isBulkOrder: boolean;
    irctcData?: IIrctcPayload;
    setForceExtraPaddingOnCart: (forceExtraPaddingOnCart: boolean) => void;
    errorData?: {
        maxAllowedCartWeightInGrams?: number;
        maxAllowedCartVolumeInCc?: number;
        restaurantName?: string;
        itemLimitExceededList?: ItemLimitExceededItem[];
    };
    showFooterOverHalfCard?: boolean;
    closeHalfCard?: VoidFunction;
    onSelectOrderTypeClick?: VoidFunction;
    cartFooterDetails?: ICartSavingsFooterDetails;
    delayTurboPaymentWidget?: boolean;
    onCtaActionOverride?: (action: string) => void;
}

function CartFooter(props: CartFooterProps): ReactElement {
    const { showFooterOverHalfCard = false, delayTurboPaymentWidget = false, onCtaActionOverride } = props;
    const isPlatformIos = useMemo(
        () => isiOSPlatform(GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || ""),
        [],
    );
    const { updateCartInProgress, doUpdateCart, reloadCart } = useContext(UpdateCartContext);

    const { setUser } = useContext(UserContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { cartState } = useContext(CartContext);
    const { data: cartFlowData } = useContext(CartFlowContext);

    const { reviewedCart: cartResponse, localCart } = cartState;
    const isQuickCheckoutEnabled = useMemo(() => isQuickCheckoutFlow(), []);
    const totalItemCount = useMemo(
        () => (isQuickCheckoutEnabled ? getMROTotalItemsCount(cartResponse as MROReviewedCart) : 0),
        [isQuickCheckoutEnabled, cartResponse],
    );
    const turboPaymentVariant = useMemo(
        () => getTurboPaymentVariantForCart(isQuickCheckoutEnabled),
        [isQuickCheckoutEnabled],
    );
    const turboPaymentVariantData = useMemo(() => ({ totalItemCount }), [totalItemCount]);
    const { nonPnrHalfcardData } = usePnrData();
    const { trackEnterPnrClick, trackFotCartErrorImpression } = useIrctcAnalytics(cartResponse);
    const { setIsIrctcPnrHalfCardOpen, setIsEnterPnr } = useContext(PnrDataContext);
    const cartFooterRef = useRef<HTMLDivElement>(null);
    const [showSlots, setShowSlots] = useState(false);

    const {
        oosItemCount,
        clearGiftingCart,
        handleRemoveOOSItems,
        handleRemoveUnserviceableItems,
        handleItemLimitExceeded,
    } = useGiftingCartFooterActions();

    const isGiftingCart = isGiftingCartSource(cartFlowData?.cartSource);
    const unServiceableItemsCount = useMemo(
        () => (isGiftingCart ? oosItemCount : props.unServiceableItemsCount),
        [isGiftingCart, oosItemCount, props.unServiceableItemsCount],
    );

    const isPopCart = isPopFlow();
    const isMROCart = isMROFlow();
    const parentCartId = (cartResponse as MROReviewedCart)?.data?.cartId;

    const isOnPremiseFulfilment = useMemo(() => isOnPremiseFulfilmentCart(cartResponse?.data ?? null), [cartResponse]);
    const onPremiseData = useMemo(() => getOnPremiseFulfilmentData(cartResponse?.data ?? null), [cartResponse]);

    //The error can occur when api call fails, user has applied payment coupon, upiIntent app is not there in the phone, payment amount is more than 2000 in case of vsc,
    //backend did not sent single click group as user ppm was empty
    const onErrorTurboPay = useCallback(() => {
        props.setIsTurboPayWidgetHidden(true);
    }, [props.setIsTurboPayWidgetHidden]);

    const { goToAddAddress, goToSelectAddress, selectedAddress, displayAddressList } = useFetchAddressOnClick(
        props?.cartTotal,
        props?.addresses,
    );

    const { onAddOrSelectAddressClick, isCartAddressSelectorDisabled } = useFetchAddressList();
    const { onImpressionAddOrSelectAddress, onClickAddOrSelectAddress } = useCartAddressNudgeAnalytics();

    const onAddOrSelectAddressWidgetClick = useCallback(() => {
        onClickAddOrSelectAddress();
        onAddOrSelectAddressClick();
    }, [onClickAddOrSelectAddress, onAddOrSelectAddressClick]);

    const proceedToPayImpressionEvent = useCallback(() => {
        onImpressionProceedToPay(!!cartState.localCart?.isCouponValid, impressionEvent);
    }, [cartState.localCart?.isCouponValid, impressionEvent]);

    const goToSimilarRestuarantAnalyticsContextValue = useMemo(
        () => goToSimilarRestuarantAnalyticsContext(cartResponse, props.cartTotal),
        [cartResponse, props.cartTotal],
    );

    const goToSimilarRestuarantHomeAnalyticsContextValue = useMemo(
        () => goToSimilarRestuarantHomeAnalyticsContext(cartResponse, props.cartTotal),
        [cartResponse, props.cartTotal],
    );

    const impressionProceedToPayButton = useCallback(() => {
        if (props.isBulkOrder) {
            onImpressionOcassionsProceedToPay(
                impressionEvent,
                JSON.stringify({ cartId: props.cartId, cartValue: props.cartTotal, bulkOrder: true }),
                props.restaurantId,
            );
            return;
        }

        impressionProceedToPay({
            impressionEvent,
            context: JSON.stringify({ cartId: props.cartId }),
            restaurantId: props.restaurantId,
        });
    }, [impressionEvent, props.cartId, props.restaurantId, props.isBulkOrder, props.cartTotal]);

    const impressionSelectDeliverySlotButton = useCallback(() => {
        if (!isEmpty(props.deliverySlots)) {
            const currentTime = new Date();
            const contextValue = props.isBulkOrder
                ? { cartId: props.cartId, bulkOrder: true }
                : { cartId: props.cartId, impressionTime: currentTime };
            impressionSelectDeliverySlot({
                impressionEvent,
                context: JSON.stringify(contextValue),
                restaurantId: props.restaurantId,
            });
        }
    }, [impressionEvent, props.cartId, props.deliverySlots, props.restaurantId, props.isBulkOrder]);

    const impressionAllDelierySlots = useCallback(() => {
        if (!isEmpty(props.deliverySlots)) {
            impressionDelierySlots({
                impressionEvent,
                context: JSON.stringify(props.deliverySlots.slots),
                restaurantId: props.restaurantId,
            });
        }
    }, [impressionEvent, props.deliverySlots, props.restaurantId]);

    const onTurboStripImpressionEvent = useCallback(
        (isNewBanner: boolean, bannerId: string, stripMsg: string) => {
            onImpressionTurboStrip(isNewBanner, bannerId, stripMsg, impressionEvent);
        },
        [impressionEvent],
    );

    const onTurboStripClickEvent = useCallback(
        (isNewBanner: boolean, bannerId: string, stripMsg: string) => {
            onClickTurboStrip(isNewBanner, bannerId, stripMsg, clickEvent);
        },
        [clickEvent],
    );

    const onTooltipImpressionEvent = useCallback(
        (campaignId: string, stripMsg?: string) => {
            onImpressionTurboTooltip(campaignId, stripMsg, impressionEvent);
        },
        [impressionEvent],
    );

    const onTooltipClickEvent = useCallback(
        (campaignId: string, stripMsg?: string) => {
            onClickTurboTooltipClose(campaignId, stripMsg, clickEvent);
        },
        [clickEvent],
    );

    useEffect(() => {
        if (props.statusCode === CART_STATUS_CODES.SUCCESS) {
            return;
        }

        if (props.statusCode === CART_STATUS_CODES.ITEM_OUTOFSTOCK) {
            if (isMROCart) {
                impressionRemoveAllOOSItemsMRO(impressionEvent, parentCartId, props.cartId);
            } else {
                impressionRemoveAllOOSItems(impressionEvent, props.cartId);
            }
        }

        if (props.ctaAction?.toLowerCase() === CART_ERROR_CTA.GOTO_SIMILAR_RESTAURANTS) {
            if (isMROCart) {
                impressionViewSimilarRestaurantCTAMRO({
                    impressionEvent,
                    parentCartId,
                    cartId: props.cartId,
                });
            }
            if (isEmpty(cartResponse?.cta_meta)) {
                impressionViewSimilarRestaurantCTA({
                    impressionEvent,
                    actionName: IMPRESSION_ACTIONS.IMPRESSION_VIEW_SIMILAR_CTA_GOTOHOME,
                    statusCode: props.statusCode,
                    context: goToSimilarRestuarantHomeAnalyticsContextValue,
                });
            } else {
                impressionViewSimilarRestaurantCTA({
                    impressionEvent,
                    context: goToSimilarRestuarantAnalyticsContextValue,
                    actionName: IMPRESSION_ACTIONS.IMPRESSION_VIEW_SIMILAR_CTA_GOTOCOLLECTIONS,
                    statusCode: props.statusCode,
                });
            }
        }
    }, [
        cartResponse,
        goToSimilarRestuarantAnalyticsContextValue,
        goToSimilarRestuarantHomeAnalyticsContextValue,
        impressionEvent,
        props.cartTotal,
        props.ctaAction,
        props.statusCode,
        props.cartId,
        isMROCart,
        parentCartId,
    ]);

    const handleMakePayment = useCallback(
        (selectedSlot?: string) => {
            props.onMakePayment(selectedSlot);
        },
        [props],
    );

    const viewDetailedBill = useCallback(() => {
        setShowSlots(false);
        props.viewDetailedBill();
    }, [props]);

    const onSlotSelect = useCallback(
        async (slotId: string) => {
            //analytics
            clickDropptDeliverySlot(
                clickEvent,
                props.restaurantId,
                JSON.stringify({
                    slots: props.deliverySlots?.slots,
                    selectedSlot: props.deliverySlots?.slots.filter((slot) => slot.slot_id === slotId)[0],
                }),
            );

            //action
            if (!updateCartInProgress) {
                const newCart = await doUpdateCart({
                    ...localCart,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    slot_id: slotId,
                });
                if (newCart?.statusCode != CART_STATUS_CODES.SUCCESS) {
                    setShowSlots(false);
                }
            }
        },
        [clickEvent, doUpdateCart, localCart, props.deliverySlots, props.restaurantId, updateCartInProgress],
    );

    const ignoreDeliveryValidityCheckFromAddress = isGiftingCart || isOnPremiseFulfilment;

    const onErrorCtaAction = useCallback(
        async (action: string) => {
            switch (action.toLowerCase()) {
                case CART_ERROR_CTA.CLEAR_CART:
                    if (isGiftingCart) {
                        clearGiftingCart();
                        break;
                    }

                    clickClearCart(props.cartTotal, clickEvent);
                    getBridgeSdk().clearLocalCart({
                        tenant: {
                            marketplaceBusinessline: "Regular",
                            marketplaceCategory: "Food",
                            marketplaceId: 1,
                        },
                    });
                    await doUpdateCart({ ...localCart, cartItems: [], mealItems: [] });
                    break;
                case CART_ERROR_CTA.REMOVE_SUPER:
                    if (!updateCartInProgress) {
                        await doUpdateCart({
                            ...localCart,
                            subscriptionItems: [],
                        });
                    }
                    break;
                case CART_ERROR_CTA.REMOVE_UNAVAILABLE_ITEMS:
                    clickRemoveAllOOSItems(props.cartTotal, clickEvent, props.cartId);
                    // TODO: handle meal items cases post integration
                    if (!updateCartInProgress) {
                        if (isGiftingCart) {
                            await handleRemoveOOSItems();
                            break;
                        }
                        const remainingAvaialableItems = getAvailableCartItems(props.cartItems);
                        // if no items are remaining clear the cart
                        if (remainingAvaialableItems.length < 1) {
                            clickClearCart(props.cartTotal, clickEvent);
                            getBridgeSdk().clearLocalCart({
                                tenant: {
                                    marketplaceBusinessline: "Regular",
                                    marketplaceCategory: "Food",
                                    marketplaceId: 1,
                                },
                            });
                            await doUpdateCart({ ...localCart, cartItems: [], mealItems: [] });
                        } else {
                            await doUpdateCart({
                                ...localCart,
                                cartItems: remainingAvaialableItems,
                            });
                        }
                    }
                    break;
                case CART_ERROR_CTA.VIEW_SIMILAR_RESTAURANTS:
                case CART_ERROR_CTA.GO_TO_HOME:
                    if (isIxigoFlow()) {
                        closeIxigoWebview();
                        break;
                    }

                    getBridgeSdk().navigateToScreen({
                        type: NavigationTypes.SCREEN,
                        link: NavigationScreenLinkTypes.HOME,
                    });
                    break;
                case CART_ERROR_CTA.GOTO_SIMILAR_RESTAURANTS:
                    if (isIxigoFlow()) {
                        closeIxigoWebview();
                        return;
                    }
                    if (isEmpty(cartResponse?.cta_meta)) {
                        clickViewSimilarRestaurantCTA({
                            clickEvent,
                            actionName: CLICK_ACTIONS.CLICK_VIEW_SIMILAR_CTA_GOTOHOME,
                            statusCode: props.statusCode,
                            context: goToSimilarRestuarantHomeAnalyticsContextValue,
                        });
                        getBridgeSdk().navigateToScreen({
                            type: NavigationTypes.SCREEN,
                            link: NavigationScreenLinkTypes.HOME,
                        });
                        return;
                    }

                    clickViewSimilarRestaurantCTA({
                        clickEvent,
                        actionName: CLICK_ACTIONS.CLICK_VIEW_SIMILAR_CTA_GOTOCOLLECTIONS,
                        statusCode: props.statusCode,
                        context: goToSimilarRestuarantAnalyticsContextValue,
                    });

                    getBridgeSdk().navigateToScreen({
                        type: NavigationTypes.SCREEN,
                        link: NavigationScreenLinkTypes.COLLECTIONS,
                        params: {
                            collectionId: cartResponse?.cta_meta?.collection_id || "",
                            restaurantId: cartResponse?.cta_meta?.restaurant_id || "",
                            cityId: cartResponse?.cta_meta?.city_id || "",
                        },
                    });
                    break;
                case CART_ERROR_CTA.SELECT_ANOTHER_SLOT:
                    setShowSlots(true);
                    break;
                case CART_ERROR_CTA.ADD_MORE_ITEMS:
                    clickAddMoreItemsCTA(
                        clickEvent,
                        props.restaurantId,
                        JSON.stringify({
                            cartId: props.cartId,
                            bulkOrder: true,
                        }),
                    );

                    getBridgeSdk().navigateToScreen({
                        type: NavigationTypes.SCREEN,
                        link: NavigationScreenLinkTypes.MENU,
                        params: {
                            id: props.restaurantId || "",
                            fromCart: true,
                        },
                    });
                    break;
                case CART_ERROR_CTA.ENTER_PNR:
                    trackEnterPnrClick();
                    setIsIrctcPnrHalfCardOpen(true);
                    break;
                case CART_ERROR_CTA.SELECT_ANOTHER_STATION:
                    getBridgeSdk().navigateToScreen({
                        type: NavigationTypes.DEEPLINK,
                        link: format(
                            IRCTC_STATION_CHANGE_DEEPLINK,
                            encodeURIComponent(buildIrctcStationChangeUrl(props.irctcData)),
                        ),
                        params: {
                            shouldCloseWebview: true,
                        },
                    });
                    break;
                case CART_ERROR_CTA.REMOVE_EXCESS_ITEMS:
                    await handleItemLimitExceeded(props.errorData?.itemLimitExceededList);
                    break;
                case CART_ERROR_CTA.GO_TO_GIFTING_HOME:
                case CART_ERROR_CTA.ADD_MORE_GIFTS:
                    goToGiftingHome();
                    break;
                case CART_ERROR_CTA.REMOVE_UNSERVICEABLE_ITEMS:
                    await handleRemoveUnserviceableItems();
                    break;
                case CART_ERROR_CTA.RETRY:
                default:
                    reloadCart();
                    break;
            }
        },
        [
            props.cartTotal,
            props.statusCode,
            props.cartItems,
            clickEvent,
            doUpdateCart,
            localCart,
            updateCartInProgress,
            cartResponse,
            goToSimilarRestuarantAnalyticsContextValue,
            reloadCart,
            goToSimilarRestuarantHomeAnalyticsContextValue,
            props.cartId,
            props.restaurantId,
            props.errorData,
            clearGiftingCart,
            isGiftingCart,
            handleRemoveOOSItems,
            handleItemLimitExceeded,
            handleRemoveUnserviceableItems,
            setIsIrctcPnrHalfCardOpen,
            trackEnterPnrClick,
            props.irctcData,
            buildIrctcStationChangeUrl,
        ],
    );

    const onClickLogin = useCallback(async (): Promise<void> => {
        try {
            clickLogin(props.cartTotal, clickEvent);
            setCartFromLoginFlowFlag();

            getBridgeSdk()
                .login({ prevPathWeb: getStaticMwebCartPath() })
                .then(async (response) => {
                    if (!response || !response.hasLoggedIn) {
                        return;
                    }

                    recordNREvent({
                        eventType: NEWRELIC_TABLES.POST_LOGIN_FLOW,
                        source: "cart-footer",
                    });
                    await syncClientHeaders();
                    // update User Context
                    getUserInfo()
                        .then((data) => data.isSome() && setUser(data.unwrap()))
                        .catch(captureError);

                    reloadCart();
                })
                .catch(() => void 0);
        } catch (err) {
            captureError(err);
        }
    }, [reloadCart, clickEvent, props.cartTotal, setUser]);

    const viewSimilarRestaurant = useCallback(() => {
        if (isIxigoFlow()) {
            closeIxigoWebview();
            return;
        }

        getBridgeSdk().navigateToScreen({
            type: NavigationTypes.SCREEN,
            link: NavigationScreenLinkTypes.HOME,
        });
    }, []);

    const clickSelectDeliverySlotButton = useCallback(() => {
        const contextValue = props.isBulkOrder ? { cartId: props.cartId, bulkOrder: true } : props.cartId;
        clickSelectDeliverySlot(clickEvent, props.restaurantId, JSON.stringify(contextValue));
        setShowSlots(true);
    }, [clickEvent, props.cartId, props.restaurantId, props.isBulkOrder]);

    const {
        serviceableAddressWithinXMetres,
        serviceableAddressOutsideXMetres,
        hasAddressWithinXMetres,
        hasAddressOutsideXMetres,
    } = getAddressConfugration(props.addresses);
    const userLoggedIn = isUserLoggedIn();
    const isJioHotstar = isJioHotstarFlow();

    const showDiscountedValueOnSlider = useMemo(() => {
        return isTurboPayDiscountValueEnabled(cartResponse);
    }, [cartResponse]);

    const isTurboPaymentMode = useMemo(
        () => props.isTurboPaymentEnabled && isEmpty(props.deliverySlots),
        [props.isTurboPaymentEnabled, props.deliverySlots],
    );

    const shouldRenderTurboPayment = isTurboPaymentMode && !props.backgroundRefreshInProgress;
    const { isTurboFooterReady, onTurboLoaded: onTurboFooterLoaded } = useTurboFooterReady(
        shouldRenderTurboPayment,
        updateCartInProgress || props.updateCartInProgress,
    );
    const showSavingsStrip = !isEmpty(props.cartFooterDetails?.savingsText) && isTurboFooterReady;

    // enable swipe for for non-turbo payment modes
    useEffect(() => {
        if (!shouldRenderTurboPayment) {
            manageSwipe(false);
        }
    }, [shouldRenderTurboPayment]);

    const computeCartFooterHeight = useCallback(() => {
        requestAnimationFrame(() => {
            const footerHeight = cartFooterRef?.current?.getBoundingClientRect().height ?? 0;
            resetElementHeightInDom(STYLE_SHEET_VAR.cartFooter);
            setElementHeightInDom(footerHeight, STYLE_SHEET_VAR.cartFooter);
        });
    }, []);

    const cartCallbackOnPaymentResponseChange = useCallback(() => {
        computeCartFooterHeight();
    }, [computeCartFooterHeight]);

    const billToCompanyWidget = useMemo(() => {
        return (
            <CartFooterPaymentWidgets
                setIsTurboPayWidgetHidden={props.setIsTurboPayWidgetHidden}
                setForceExtraPaddingOnCart={props.setForceExtraPaddingOnCart}
            />
        );
    }, [props.setIsTurboPayWidgetHidden, props.setForceExtraPaddingOnCart]);

    const { registerCrouton } = useContext(CartOverlaysContext);

    useEffect(() => {
        if (props.canShowCrouton && !isEmpty(props.crouton)) {
            registerCrouton({
                id: CroutonIds.FOOTER_CROUTON,
                priority: CroutonPriority.PRIORITY_ORDER_FOUR,
                content: {
                    title: props.crouton?.message,
                    icon: props.crouton?.icon,
                    setCroutonVisibility: props.setCroutonVisibility,
                    buttonClassName: styles.footerCroutonButton,
                },
            });
        }
    }, [props.canShowCrouton, props.crouton, props.setCroutonVisibility, registerCrouton]);

    const renderPaymentWidgetContent = useCallback(() => {
        if (shouldRenderTurboPayment && delayTurboPaymentWidget) {
            return <TurboPaymentWidgetShimmerV2 />;
        }

        const savingsStrip =
            showSavingsStrip && props.cartFooterDetails ? (
                <CartSavingsFooterStrip details={props.cartFooterDetails} />
            ) : null;

        // Turbo and Pre-order dont work together, so dont show turbo if pre order flow
        if (shouldRenderTurboPayment) {
            return (
                <>
                    {savingsStrip}
                    <TurboPaymentCartWrapper
                        uniqueId={cartState.uniqueIdForCart}
                        onPaymentMethodChange={handleMakePayment}
                        onError={onErrorTurboPay}
                        hideEducationComms={props.isTurboPayTooltipHidden || isJioHotstar}
                        showLoader={updateCartInProgress}
                        showDiscountedValueOnSlider={showDiscountedValueOnSlider}
                        isTurboV2
                        cartCallbackOnTurboPaymentChange={cartCallbackOnPaymentResponseChange}
                        setForceExtraPaddingOnCart={props.setForceExtraPaddingOnCart}
                        billAmountShownOnCart={String(props.cartTotal)}
                        setIsTurboPayWidgetHidden={props.setIsTurboPayWidgetHidden}
                        onTurboStripImpressionEvent={onTurboStripImpressionEvent}
                        onTurboStripClickEvent={onTurboStripClickEvent}
                        onTooltipImpressionEvent={onTooltipImpressionEvent}
                        onTooltipClickEvent={onTooltipClickEvent}
                        onCvvPopupOpen={props.closeHalfCard}
                        variant={turboPaymentVariant}
                        variantData={turboPaymentVariantData}
                        onTurboLoaded={onTurboFooterLoaded}
                        hideTurboStrip={!!savingsStrip}
                    />
                </>
            );
        }

        return (
            <>
                {savingsStrip}
                <PaymentWidget
                    cartTotal={props.cartTotal}
                    tipAmount={props.tipAmount}
                    makePayment={handleMakePayment}
                    canPlaceOrderFromCart={props.canPlaceOrderFromCart}
                    viewDetailedBill={viewDetailedBill}
                    makePaymentButtonRef={props.makePaymentButtonRef}
                    qFoodSLA={props?.qFoodSLA}
                    onImpressionProceedToPay={proceedToPayImpressionEvent}
                    deliverySlots={props.deliverySlots}
                    onSlotSelect={onSlotSelect}
                    showSlots={showSlots}
                    setShowSlots={setShowSlots}
                    selectDeliverySlot={clickSelectDeliverySlotButton}
                    analyticsEvents={{
                        impressionProceedToPay: impressionProceedToPayButton,
                        impressionSelectDeliverySlotButton: impressionSelectDeliverySlotButton,
                        impressionAllDelierySlots: impressionAllDelierySlots,
                    }}
                    updateCartInProgress={props.updateCartInProgress}
                    isBulkOrder={props.isBulkOrder}
                    billToCompanyWidget={billToCompanyWidget}
                    isQuickCheckoutEnabled={isQuickCheckoutEnabled}
                    totalItemCount={totalItemCount}
                />
            </>
        );
    }, [
        billToCompanyWidget,
        cartCallbackOnPaymentResponseChange,
        cartState.uniqueIdForCart,
        clickSelectDeliverySlotButton,
        delayTurboPaymentWidget,
        handleMakePayment,
        impressionAllDelierySlots,
        impressionProceedToPayButton,
        impressionSelectDeliverySlotButton,
        isJioHotstar,
        onErrorTurboPay,
        onSlotSelect,
        onTooltipClickEvent,
        onTooltipImpressionEvent,
        onTurboFooterLoaded,
        onTurboStripClickEvent,
        onTurboStripImpressionEvent,
        proceedToPayImpressionEvent,
        props.canPlaceOrderFromCart,
        props.cartFooterDetails,
        props.cartTotal,
        props.closeHalfCard,
        props.deliverySlots,
        props.isBulkOrder,
        props.isTurboPayTooltipHidden,
        props.makePaymentButtonRef,
        props.qFoodSLA,
        props.setForceExtraPaddingOnCart,
        props.setIsTurboPayWidgetHidden,
        props.tipAmount,
        props.updateCartInProgress,
        shouldRenderTurboPayment,
        showDiscountedValueOnSlider,
        showSavingsStrip,
        showSlots,
        updateCartInProgress,
        viewDetailedBill,
        isQuickCheckoutEnabled,
        totalItemCount,
        turboPaymentVariant,
        turboPaymentVariantData,
    ]);

    const renderOnPremiseContent = useCallback(() => {
        if (isOnPremiseFulfilment && !onPremiseData?.order_preference && props.onSelectOrderTypeClick) {
            return <PremiseSelectOrderType onSelectOrderTypeClick={props.onSelectOrderTypeClick} />;
        } else {
            return renderPaymentWidgetContent();
        }
    }, [
        isOnPremiseFulfilment,
        onPremiseData?.order_preference,
        props.onSelectOrderTypeClick,
        renderPaymentWidgetContent,
    ]);

    const onEnterPnrCtaClick = useCallback(() => {
        trackEnterPnrClick();
        setIsIrctcPnrHalfCardOpen(true);
        setIsEnterPnr(false);
    }, [trackEnterPnrClick, setIsIrctcPnrHalfCardOpen, setIsEnterPnr]);

    const isIrctcErrorState = !!props.irctcData && props.statusCode !== CART_STATUS_CODES.SUCCESS && !showSlots;
    useEffect(() => {
        if (isIrctcErrorState) {
            trackFotCartErrorImpression(props.statusCode);
        }
    }, [isIrctcErrorState, props.statusCode, trackFotCartErrorImpression]);

    const renderSuccessContent = useMemo(() => {
        if (isOnPremiseFulfilment) {
            return renderOnPremiseContent();
        }
        if (!isEmpty(nonPnrHalfcardData) && isEmpty(cartResponse?.data?.irctc_data?.pnr_number)) {
            return <EnterPnr onEnterPnrCtaClick={onEnterPnrCtaClick} nonPnrHalfcardData={nonPnrHalfcardData} />;
        }

        // if user has selected address at listing
        if (selectedAddress) {
            // if the user selected address is serviceable
            if (selectedAddress.delivery_valid === 1 || ignoreDeliveryValidityCheckFromAddress) {
                return renderPaymentWidgetContent();
            } else if (showFooterOverHalfCard) {
                return (
                    <SuperButton
                        variant="fullwidth"
                        buttonType="tertiary"
                        buttonSize="medium"
                        onClick={props.closeHalfCard}
                        ariaLabel={GOT_IT}
                    >
                        {GOT_IT}
                    </SuperButton>
                );
            } else {
                // if user selected address is not serviceable
                return (
                    <UnServiceableCartFooterWidget
                        selectedAddress={selectedAddress}
                        onChangeAddress={goToSelectAddress}
                        onViewSimilarRestaurant={viewSimilarRestaurant}
                    />
                );
            }
        } else if (isCartAddressSelectorDisabled) {
            return (
                <AddSelectAddressWidget
                    onCtaClick={onAddOrSelectAddressWidgetClick}
                    onImpression={onImpressionAddOrSelectAddress}
                    isJioHotstarFlow={isJioHotstar}
                />
            );
        } else {
            // if user has selected location at listing
            if (hasAddressWithinXMetres) {
                if (!isEmpty(serviceableAddressWithinXMetres)) {
                    // serviceable to multiple location with add /select address
                    return (
                        <AddressSelectorWidget
                            selectAddress={goToSelectAddress}
                            addNewAddress={goToAddAddress}
                            addresses={displayAddressList}
                        />
                    );
                } else {
                    // new location
                    return (
                        <NewLocationWidget
                            addNewAddress={goToAddAddress}
                            onChangeAddress={goToSelectAddress}
                            deliveryLocation={{
                                address: "",
                                lat: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat) || "",
                                lng: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng) || "",
                            }}
                        />
                    );
                }
            } else if (hasAddressOutsideXMetres) {
                if (!isEmpty(serviceableAddressOutsideXMetres)) {
                    // New location to add and select new address
                    return (
                        <NewLocationWidget
                            addNewAddress={goToAddAddress}
                            onChangeAddress={goToSelectAddress}
                            showChangeAddress
                            deliveryLocation={{
                                address: "",
                                lat: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat) || "",
                                lng: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng) || "",
                            }}
                        />
                    );
                } else {
                    // New location to add new address
                    return (
                        <NewLocationWidget
                            addNewAddress={goToAddAddress}
                            onChangeAddress={goToSelectAddress}
                            deliveryLocation={{
                                address: "",
                                lat: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat) || "",
                                lng: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng) || "",
                            }}
                        />
                    );
                }
            } else {
                // New location to add new address
                return (
                    <NewLocationWidget
                        addNewAddress={goToAddAddress}
                        onChangeAddress={goToSelectAddress}
                        deliveryLocation={{
                            address: "",
                            lat: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat) || "",
                            lng: GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng) || "",
                        }}
                    />
                );
            }
        }
    }, [
        isOnPremiseFulfilment,
        selectedAddress,
        isCartAddressSelectorDisabled,
        renderOnPremiseContent,
        ignoreDeliveryValidityCheckFromAddress,
        showFooterOverHalfCard,
        renderPaymentWidgetContent,
        props.closeHalfCard,
        goToSelectAddress,
        viewSimilarRestaurant,
        onAddOrSelectAddressWidgetClick,
        onImpressionAddOrSelectAddress,
        isJioHotstar,
        hasAddressWithinXMetres,
        hasAddressOutsideXMetres,
        serviceableAddressWithinXMetres,
        goToAddAddress,
        displayAddressList,
        serviceableAddressOutsideXMetres,
        props.closeHalfCard,
        props.backgroundRefreshInProgress,
        showFooterOverHalfCard,
        onEnterPnrCtaClick,
        nonPnrHalfcardData,
        cartResponse?.data?.irctc_data?.pnr_number,
    ]);

    // Set default ctaAction for GIFTING_FOOD_CART_POST_ORDER_FAILURE
    const ctaAction = useMemo(() => {
        if (props.statusCode === CART_STATUS_CODES.GIFTING_FOOD_CART_POST_ORDER_FAILURE && !props.ctaAction) {
            return CART_ERROR_CTA.ADD_MORE_GIFTS as unknown as CtaAction;
        }
        return props.ctaAction;
    }, [props.statusCode, props.ctaAction]);

    const { isCookingInstructionsFocused } = useContext(CookingInstructionsFocusContext);

    const renderContent = useMemo(() => {
        if (isJioHotstarFlow() && isCookingInstructionsFocused) {
            return null;
        }
        if (!userLoggedIn) {
            return <LoginWidget onLogin={onClickLogin} isJioHotstarFlow={isJioHotstar} isIxigoFlow={isIxigoFlow()} />;
        }

        switch (props.statusCode) {
            case CART_STATUS_CODES.SUCCESS:
                return renderSuccessContent;
            default:
                if (showSlots) {
                    return renderSuccessContent;
                }
                return (
                    <ErrorWidget
                        action={onCtaActionOverride ?? onErrorCtaAction}
                        statusCode={props.statusCode}
                        statusMessage={props.statusMessage}
                        ctaIcon={props.ctaIcon}
                        ctaText={props.ctaText}
                        ctaAction={ctaAction}
                        titleMessage={props.titleMessage}
                        unServiceableItemsCount={unServiceableItemsCount}
                        isIrctcCart={!!props.irctcData}
                        isPopCart={isPopCart}
                        errorData={props.errorData}
                    />
                );
        }
    }, [
        onClickLogin,
        onErrorCtaAction,
        ctaAction,
        props.ctaIcon,
        props.ctaText,
        props?.irctcData,
        props.statusCode,
        props.statusMessage,
        props.titleMessage,
        unServiceableItemsCount,
        renderSuccessContent,
        showSlots,
        userLoggedIn,
        isJioHotstar,
        isPopCart,
        props.errorData,
        isCookingInstructionsFocused,
    ]);

    useEffect(() => {
        computeCartFooterHeight();
    });

    const footerContent = (
        <FooterContainer
            ref={cartFooterRef}
            showFooterOverHalfCard={showFooterOverHalfCard}
            isPlatformIos={isPlatformIos}
        >
            {renderContent}
        </FooterContainer>
    );

    if (showFooterOverHalfCard) {
        return <Portal>{footerContent}</Portal>;
    }

    return footerContent;
}

export default memo(CartFooter);
